// @flow
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet-async';
import { Editor, ContentState, convertToRaw } from 'draft-js';

import { Datalayer } from '@finect/front-gtm';
import { Container } from '@finect/tabular-core/Container';
import { ContainerInner } from '@finect/tabular-core/ContainerInner';
import { Side } from '@finect/tabular-core/Side';
import { AppLink } from '@finect/tabular-core/AppLink';
import { HTML } from '@finect/tabular-components/HTML';
import { LiveBanner } from '@finect/tabular-components/LiveBanner';
import { MainContent } from '@finect/tabular-core/MainContent';
import { Flex } from '@finect/tabular-core/Flex';
import { BottomAds } from '@finect/tabular-components/Ads';
import { Small } from '@finect/tabular-core/Small';
import { H1 } from '@finect/tabular-core/Headings';
import { withSubscriptions } from '@finect/front-core/User';
import type { WithSubscriptionsProps } from '@finect/front-core/User';
import type { QuestionType, TagType, UserType } from '../../types';
import { openOverlay } from '../../store/overlay/actions';
import {
  HTMLDescription,
  Disclaimer,
  QuestionBlock,
  QuestionPreview,
  QuestionFilters,
  FilterSelect,
  CurrentQuestions,
  TabMenu,
  QuestionDetail,
  QuestionDetailInner,
  Navigation,
  NewQuestionBlock,
  NewQuestion,
  ShowMoreBlock,
  ShowMore,
  Spinner,
  Follow,
  NotTopicQuestions,
  // NotFollowedQuestions,
  LoadingBar,
  Heading,
  RandomTags,
  Stats
} from '../../components';
import { getQuestions, voteQuestion, preFetchedQuestions } from '../../store/questions/actions';
import { followTag, unfollowTag } from '../../store/tags/actions';

const mapStateToProps = (state: Object) => ({
  user: state.user.user,
  tags: state.tags,
  questions: state.questions,
  // savedQuestions: state.user.subscriptions.questions,
  likedQuestions: state.user.likes.questions
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      getQuestions,
      voteQuestion,
      followTag,
      unfollowTag,
      preFetchedQuestions,
      openOverlay
    },
    dispatch
  )
});

type Props = {
  match: Object,
  type?: string,
  user: UserType,
  staticContext?: Object,
  actions: Object,
  questions: {
    items: { [key: string]: QuestionType },
    list: Array<string>,
    more: boolean,
    type: string,
    isTag: number
  },
  tags: {
    [key: number]: TagType
  },
  tag: TagType,
  tagSlug: string,
  history: Object,
  location: Object,
  // savedQuestions: Array<string>,
  likedQuestions: Array<string>
} & WithSubscriptionsProps;

type State = {
  fetchingMore: boolean,
  listEnd: boolean,
  loading: boolean,
  previousPath: string
};

class TopicList extends React.Component<Props, State> {
  titleEditor: Editor;
  filterSelect: HTMLSelectElement;

  state = {
    fetchingMore: false,
    listEnd: false,
    loading: false,
    previousPath: ''
  };

  static loadInitialData(store, cookie, match, params) {
    return store.dispatch(getQuestions(params ? params.type : '', match.params.tagId, null, cookie));
  }

  componentDidMount() {
    const { tag, type, questions, actions } = this.props;

    if (questions.type !== type || !questions.isTag || questions.isTag !== tag.id) {
      this.setState({ loading: true });
      actions.getQuestions(type, tag.id);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const { type, tag, questions, location } = this.props;
    const { type: nextType, tag: nextTag, questions: nextQuestions } = nextProps;
    if (type !== nextType || tag.id !== nextTag.id) {
      this.props.actions.getQuestions(nextType, nextTag.id);
      const previousPath = tag !== nextTag ? '' : location.pathname.replace(/\/\s*$/, '');
      this.setState({
        listEnd: false,
        loading: true,
        previousPath
      });
    }
    if (this.state.fetchingMore) {
      const newState = {};
      newState.fetchingMore = false;
      if (!nextQuestions.more) newState.listEnd = true;
      this.setState(newState);
    }
    if (
      (questions.type !== nextQuestions.type && this.state.loading) ||
      (!questions.isTag && this.state.loading) ||
      (questions.isTag !== nextQuestions.isTag && this.state.loading)
    ) {
      this.setState({ loading: false, previousPath: '' });
    }
  }

  pagination = () => {
    const offset = this.props.questions.list.length;
    this.props.actions.getQuestions(this.props.type, this.props.tag.id, offset);
    this.setState({ fetchingMore: true });
  };

  followTag = () => {
    this.props.actions.followTag(this.props.tag.id);
  };

  unfollowTag = () => {
    this.props.actions.unfollowTag(this.props.tag.id);
  };

  focusTitleEditor = () => {
    this.titleEditor.focus();
  };

  handleReturn = (value: string) => {
    const { tag } = this.props;

    localStorage.setItem(
      'questionTitle',
      JSON.stringify({
        content: JSON.stringify(convertToRaw(ContentState.createFromText(value))),
        timestamp: new Date().getTime()
      })
    );

    localStorage.setItem(
      'questionTags',
      JSON.stringify({
        content: JSON.stringify([tag]),
        timestamp: new Date().getTime()
      })
    );
    this.props.history.push('/preguntas/nueva-pregunta');
  };

  handleChange = () => {
    this.props.history.push(this.filterSelect.value);
  };

  goBack = () => {
    this.props.history.goBack();
  };

  handleLike = (questionId: string) => {
    const { handleEntityFollow, likedQuestions, user, actions } = this.props;

    if (user.isLoggedIn) {
      handleEntityFollow('question', 'like', questionId, !likedQuestions.includes(questionId));
      return actions.voteQuestion(questionId, !likedQuestions.includes(questionId));
    }
    const blob = {
      copy: 'Accede a Finect para realizar esta acción.',
      action: 'like',
      target: {
        id: questionId,
        type: 'question'
      }
    };
    actions.openOverlay(blob);
  };

  // handleFollow = (questionId: string) => {
  //   const { handleEntityFollow, savedQuestions, user, actions } = this.props;

  //   if (user.isLoggedIn) {
  //     return handleEntityFollow('question', 'subscribe', questionId, !savedQuestions.includes(questionId));
  //   }
  //   const blob = {
  //     copy: 'Accede a Finect para realizar esta acción.',
  //     action: 'like',
  //     target: {
  //       id: questionId,
  //       type: 'question'
  //     }
  //   };
  //   actions.openOverlay(blob);
  // };

  render() {
    const {
      user,
      tags,
      tagSlug,
      tag = {},
      history,
      questions,
      actions,
      // savedQuestions = [],
      likedQuestions = []
    } = this.props;
    const { loading, previousPath } = this.state;
    const { pathname } = this.props.location;
    const path = pathname.replace(/\/\s*$/, '');
    const currentPath = loading ? previousPath : path;
    const tab = currentPath.replace(`/preguntas/temas/${tag.id}/${tagSlug}`, '');
    const hasTags = Object.keys(tags).length !== 0;
    const hasQuestions = Boolean(this.props.questions.list.length);
    const loadedQuestions = this.props.questions.type;
    const { fromApp } = history.location.state || {};

    const filters = [
      { name: 'Últimas', path: `/preguntas/temas/${tag.id}/${tagSlug}` },
      {
        name: 'Más activas',
        path: `/preguntas/temas/${tag.id}/${tagSlug}/mas-activas`,
        nofollow: true
      }
    ];
    const privateFilters = [
      // {
      //   name: 'Siguiendo',
      //   path: `/preguntas/temas/${tag.id}/${tagSlug}/siguiendo`,
      //   nofollow: true
      // }
    ];
    const currentFilters = user.isLoggedIn ? [...filters, ...privateFilters] : [...filters];

    let whiteState;

    if (!hasQuestions) {
      switch (tab) {
        // case '/siguiendo': {
        //   if (loadedQuestions === 'followedQuestions') {
        //     whiteState = <NotFollowedQuestions topic={tag} />;
        //   }
        //   break;
        // }
        case '/mas-activas':
        default:
          if (loadedQuestions === 'active' || loadedQuestions === 'latest') {
            whiteState = <NotTopicQuestions topic={tag} />;
          }
          break;
      }
    }
    const { stats } = tag;

    return (
      <Container>
        <Helmet>
          <meta charSet="utf-8" />
          <title>Preguntas sobre {tag && tag.name} - Preguntas Finect</title>
          <meta name="description" content={`Preguntas sobre ${tag && tag.name}`} />
          <meta name="robots" content="noindex, follow" />
        </Helmet>

        <Datalayer id="Scene" type="ready" params={{ pageType: 'questionsTopic', pageId: tag.id }} />
        <Datalayer id="Scene" type="pageType" params={{ pageType: 'questionsTopic', pageId: tag.id }} />

        <LoadingBar loading={loading} />

        <LiveBanner />

        <Heading extraMargin>
          <Navigation fromApp={fromApp} goBack={this.goBack} />

          <H1 large>Preguntas sobre {tag && tag.name}</H1>
          <Stats stats={stats} />
          {tag.description ? <HTML wrapper={HTMLDescription} html={tag.description} /> : null}
          <Flex align="center" justify="space-between">
            {user.isLoggedIn ? (
              <Follow
                buttonMini
                following={tag.following}
                followAction={tag.following ? this.unfollowTag : this.followTag}
              />
            ) : null}
            <AppLink href="/preguntas/temas">
              <Small>Ver más temas</Small>
            </AppLink>
          </Flex>
        </Heading>
        <ContainerInner>
          <MainContent noMargin>
            <NewQuestionBlock>
              <QuestionDetail prePublish>
                <QuestionDetailInner>
                  <NewQuestion
                    placeholder="¿Tienes una duda? Busca respuestas en Finect"
                    tagId={tag.id}
                    handleReturn={this.handleReturn}
                  />
                </QuestionDetailInner>
              </QuestionDetail>
            </NewQuestionBlock>
          </MainContent>
        </ContainerInner>
        <ContainerInner>
          <MainContent>
            <QuestionFilters>
              <TabMenu options={filters} path={currentPath} />
              {user.isLoggedIn ? <TabMenu options={privateFilters} path={currentPath} /> : null}
            </QuestionFilters>
            <FilterSelect
              onChange={this.handleChange}
              innerRef={title => (this.filterSelect = title)}
              defaultValue={currentPath}
            >
              {currentFilters.map((filter, index) => (
                <option key={index} value={filter.path}>
                  {filter.name}
                </option>
              ))}
            </FilterSelect>
            {questions.isTag ? (
              <CurrentQuestions>
                {whiteState ? whiteState : null}
                <QuestionBlock>
                  {questions.list.map((id) => {
                    const question = questions.items[id];
                    return (
                      <QuestionPreview
                        key={id}
                        currentTag={tag.id}
                        question={question}
                        voted={likedQuestions.includes(id)}
                        handleLike={() => this.handleLike(id)}
                        // following={savedQuestions.includes(id)}
                        // handleFollow={() => this.handleFollow(id)}
                        blockVote={question.author === user.id || !user.isLoggedIn}
                        user={user}
                        backText={`Volver a preguntas sobre: "${tag && tag.name}"`}
                      />
                    );
                  })}
                  {questions.more && !this.state.fetchingMore ? (
                    <ShowMoreBlock>
                      <ShowMore onClick={this.pagination}>Mostrar más preguntas</ShowMore>
                    </ShowMoreBlock>
                  ) : null}
                  {this.state.fetchingMore ? <Spinner /> : null}
                  {this.state.listEnd ? <ShowMoreBlock light>Has llegado al final de la lista.</ShowMoreBlock> : null}
                </QuestionBlock>
              </CurrentQuestions>
            ) : null}

            <Disclaimer />
          </MainContent>
          <Side padding>
            {hasTags && questions.isTag === tag.id ? (
              <RandomTags
                hide="miniDesktop"
                title="Otros temas relacionados"
                tags={tags}
                ignoreId={tag.id}
                register={actions.preFetchedQuestions}
              />
            ) : null}
          </Side>
        </ContainerInner>

        <BottomAds path="Finect_Content_1" sticky closeable />
      </Container>
    );
  }
}

export default withSubscriptions(connect(mapStateToProps, mapDispatchToProps)(TopicList), ['profiles', 'products']);
